import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import path from 'path'
import BrandDirectory from 'components/BrandDirectory'
import BrandSearch from 'components/BrandSearch'
import arrayUtils from 'utils/array'
import constants from '../../constants'

const BrandDirectoryWrap = ({
  brands,
  labels,
  search,
  services,
  villageName,
}) => {
  const labelsFull = Object.assign(
    {
      resultLabel: search.resultLabelSing,
      resultsLabel: search.resultLabelPlural,
    },
    labels,
    search
  )

  const servicesFormatted = useMemo(() => {
    return arrayUtils.servicesFormatted(services)
  }, [labelsFull])

  const brandsFormatted = Array.from(
    brands.edges,
    ({ node: { openStatus, slug, nodeLocale, pageType, ...brand } }) => {
      const { villageSlug } = labels

      const statusLabels = {
        [constants.OPENING_STATUS]: labels.openingLabel,
        [constants.CLOSING_STATUS]: labels.closingLabel,
        [constants.REOPENING_STATUS]: labels.reopeningLabel,
        [constants.CLOSED_STATUS]: labels.closedLabel,
      }

      return {
        statusLabel: statusLabels[openStatus],
        link: path.join(
          villageSlug,
          nodeLocale,
          (pageType || {}).pageTypeSlug || 'brands',
          slug
        ),
        ...brand,
      }
    }
  )
    .filter((brand) => {
      if (labelsFull.services && labelsFull.services.length > 0) {
        return !!brand.services
      }

      return true
    })
    .filter((brand) => !brand.hideBrand)

  const [showBrandSearch, setShowBrandSearch] = useState(false)
  const toggleBrandSearch = () => {
    // @ts-ignore
    window.modalIsOpened = !showBrandSearch
    setShowBrandSearch(!showBrandSearch)
  }

  const hasBrands = !!brandsFormatted.length

  return (
    <>
      {hasBrands && (
        <BrandDirectory
          brands={brandsFormatted}
          villageName={villageName}
          services={servicesFormatted}
          {...labelsFull}
          toggleBrandSearch={toggleBrandSearch}
          key="directory"
        />
      )}
      {hasBrands && showBrandSearch && (
        <BrandSearch
          brands={brandsFormatted}
          villageName={villageName}
          onClose={toggleBrandSearch}
          {...search}
          key="search"
        />
      )}
    </>
  )
}

BrandDirectoryWrap.propTypes = {
  brands: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.shape({
          name: PropTypes.string,
          slug: PropTypes.string,
          openStatus: PropTypes.string,
          categories: PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string,
              label: PropTypes.string,
            })
          ),
          nodeLocale: PropTypes.string,
          pageType: PropTypes.shape({
            pageTypeSlug: PropTypes.string,
          }),
        }),
      })
    ),
  }),
  villageName: PropTypes.string,
  labels: PropTypes.shape({
    allCategoriesCopy: PropTypes.string,
    bodyCopy: PropTypes.string,
    header: PropTypes.string,
    loadMoreCTACopy: PropTypes.string,
    searchNoResults: PropTypes.string,
    searchPlaceholder: PropTypes.string,
    searchTitle: PropTypes.string,
    openingLabel: PropTypes.string,
    closingLabel: PropTypes.string,
    closedLabel: PropTypes.string,
    reopeningLabel: PropTypes.string,
    villageSlug: PropTypes.string,
  }),
  search: PropTypes.shape({
    categorySearchLabel: PropTypes.string,
    ctaLabel: PropTypes.string,
    headline: PropTypes.string,
    noResults: PropTypes.string,
    promptText: PropTypes.string,
    resultLabelPlural: PropTypes.string,
    resultLabelSing: PropTypes.string,
  }),
  services: PropTypes.shape({
    labels: PropTypes.arrayOf(
      PropTypes.shape({
        servicesDropdownLabel: PropTypes.string,
      })
    ),
    contactMethod: PropTypes.arrayOf({
      bookingUrl: PropTypes.string,
      emailAddressLabel: PropTypes.string,
      phoneNumberLabel: PropTypes.string,
      productCatalogueLabel: PropTypes.string,
      whatsapp: PropTypes.string,
      bookingUrlIcon: PropTypes.shape({
        code: PropTypes.shape({
          code: PropTypes.string,
        }),
      }),
      emailIcon: PropTypes.shape({
        code: PropTypes.shape({
          code: PropTypes.string,
        }),
      }),
      productCatalogueIcon: PropTypes.shape({
        code: PropTypes.shape({
          code: PropTypes.string,
        }),
      }),
      whatsappIcon: PropTypes.shape({
        code: PropTypes.shape({
          code: PropTypes.string,
        }),
      }),
    }),
  }),
}

BrandDirectoryWrap.defaultProps = {
  brands: {
    edges: [
      {
        node: {
          name: '',
          slug: '',
          openStatus: '',
          categories: [
            {
              name: '',
              label: '',
            },
          ],
          nodeLocale: '',
          pageType: {
            pageTypeSlug: '',
          },
          village: {
            villageLabels: [
              {
                openingLabel: '',
                closingLabel: '',
                reopeningLabel: '',
              },
            ],
            villageSlug: '',
          },
        },
      },
    ],
  },
  villageName: '',
  labels: {
    allCategoriesCopy: '',
    bodyCopy: '',
    header: '',
    loadMoreCTACopy: '',
    searchNoResults: '',
    searchPlaceholder: '',
    searchTitle: '',
  },
  search: {
    categorySearchLabel: '',
    ctaLabel: '',
    headline: '',
    noResults: '',
    promptText: '',
    resultLabelPlural: '',
    resultLabelSing: '',
  },
  services: {
    labels: [
      {
        servicesDropdownLabel: '',
      },
    ],
    service: [
      {
        bookingUrl: '',
        emailAddressLabel: '',
        phoneNumberLabel: '',
        productCatalogueLabel: '',
        whatsapp: '',
        bookingUrlIcon: {
          code: {
            code: '',
          },
        },
        emailIcon: {
          code: {
            code: '',
          },
        },
        productCatalogueIcon: {
          code: {
            code: '',
          },
        },
        whatsappIcon: {
          code: {
            code: '',
          },
        },
      },
    ],
  },
}

export default BrandDirectoryWrap
